/**
 * The debug panel: everything that used to require a terminal.
 *
 * Once the desktop launcher runs the servers hidden, the console output is
 * somewhere nobody will look. So the facts that actually matter when something
 * is wrong — is the socket up, what revision are we on, how many tabs are
 * attached, what did the last command do — have to be reachable from the app.
 */

import { formatBuild } from './build-info.js';

const HISTORY_LIMIT = 60;

export function createDebugPanel({ registry, getBridge = () => null, getBuild = () => null } = {}) {
  const root = document.getElementById('debug');
  const facts = document.getElementById('debug-facts');
  const history = document.getElementById('debug-history');

  document.getElementById('debug-close').addEventListener('click', () => setOpen(false));

  /** @type {Array<{cmd:string, ok:boolean, detail?:string, at:Date}>} */
  const entries = [];
  let connection = { state: 'connecting', note: '' };

  function setOpen(open) {
    root.dataset.open = String(open);
    if (open) render();
  }

  const isOpen = () => root.dataset.open === 'true';

  function setConnection(state, note) {
    connection = { state, note: note ?? '' };
    if (isOpen()) render();
  }

  function record(cmd, ok, detail) {
    entries.unshift({ cmd, ok, detail, at: new Date() });
    // Newest first, and bounded — this runs for the life of the tab.
    if (entries.length > HISTORY_LIMIT) entries.length = HISTORY_LIMIT;
    if (isOpen()) render();
  }

  function refresh() {
    if (isOpen()) render();
  }

  function render() {
    const bridge = getBridge();
    const stats = bridge?.stats ?? {};
    const authoritativeHistory = bridge?.history ?? {};
    const persistence = stats.persistence;
    const failures = entries.filter((entry) => !entry.ok).length;

    let storage = '—';
    if (persistence) {
      const state = persistence.healthy ? 'healthy' : 'degraded';
      const detail = persistence.recovery ?? persistence.lastError;
      storage = `${persistence.source} · ${state}${detail ? ` — ${detail}` : ''}`;
    }

    const build = getBuild?.();
    const rows = [
      ['bridge', connection.state === 'connected' ? '● connected' : `○ ${connection.state}`],
      ['url', bridge?.url ?? '—'],
      ['build', build?.commit ? formatBuild(build) : '—'],
      ['revision', stats.revision ?? '—'],
      ['viewers', stats.viewers ?? '—'],
      ['logged', stats.logged ?? '—'],
      ['undo depth', authoritativeHistory.undoDepth ?? 0],
      ['undo label', authoritativeHistory.undoLabel ?? '—'],
      ['redo depth', authoritativeHistory.redoDepth ?? 0],
      ['redo label', authoritativeHistory.redoLabel ?? '—'],
      ['storage', storage],
      ['objects', registry?.size ?? '—'],
      ['errors', failures === 0 ? 'none' : String(failures)],
    ];

    facts.replaceChildren();
    for (const [label, value] of rows) {
      const dt = document.createElement('dt');
      dt.textContent = label;
      const dd = document.createElement('dd');
      dd.textContent = String(value);
      if (label === 'bridge' && connection.state === 'connected') dd.className = 'ok-dot';
      facts.append(dt, dd);
    }

    history.replaceChildren();
    if (entries.length === 0) {
      const none = document.createElement('div');
      none.className = 'time';
      none.textContent = 'no commands yet';
      history.append(none);
      return;
    }

    for (const entry of entries) {
      const line = document.createElement('div');
      if (!entry.ok) line.className = 'fail';

      const time = document.createElement('span');
      time.className = 'time';
      time.textContent = `${entry.at.toLocaleTimeString([], { hour12: false })} `;

      line.append(time, document.createTextNode(`${entry.ok ? '✓' : '✗'} ${entry.cmd}`));
      // The error text is the whole reason to look here after something fails.
      if (entry.detail) line.append(document.createTextNode(` — ${entry.detail}`));
      history.append(line);
    }
  }

  return {
    setOpen,
    toggle: () => setOpen(!isOpen()),
    setConnection,
    record,
    refresh,
  };
}
